import React, { useCallback, useState, useMemo, useEffect, Fragment } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Box,
  alpha,
  styled,
  Tooltip,
  Checkbox,
  Button,
} from "@mui/material";
import type {
  IPositionOverride,
  IPairingEventGroup,
  IFlight,
  IUser,
  IFullPairingPosition,
  IPositioningFlight,
  INonFlyingAssignment,
  IPairingRest,
  IPairingLogicModifier,
  IHotel,
  IPairingHotel,
  IGroundEvent,
  IPairing,
  IRegLimitEvaluation,
} from "@vmosolutions/oc-interfaces";
import { isEpochDate, LightenDarkenColor, padNumber, convertMilli2DaysHrsMin } from "src/utils/index";
import { FlightChip } from "../../Flights/FlightChip";
import { NonFlightChip } from "../../Pairings/NonFlightChip";
import { PosFlightChip } from "../../Pairings/PosFlightChip";
// Styles
import type { Theme } from "@mui/material";
import { useTheme, Popover, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton } from "../../IconButton/IconButton";
import { LocatorChips } from "src/pages/CrewTravel/LocatorChips";
import classNames from "classnames";
// Hotel related imports
import { SelectField } from "../../FormikComponents/SelectField";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
// Helpers
import _, { cloneDeep } from "lodash";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { useDispatch, useSelector } from "react-redux";
import { openSelectedEventWindow } from "src/redux/actions/Mosaic.Actions";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { FlightDropTarget } from "./FlightDropTarget";
import type { ILMGroupChipProps } from "../../LogicModifiers/LMGroupChip";
import { LMGroupChip } from "../../LogicModifiers/LMGroupChip";
import type { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { convertFlightToPosFlight, convertPosFlightToFlight } from "./ConvertFlights";

import { DateDisplay } from "src/components/DateDisplay";

// Event Table Components
import { FlightPositionOverride } from "./EventTableComponents/FlightPositionOverride";
import { DutyPeriodSummary } from "./EventTableComponents/DutyPeriodSummary";
import { DutyPeriodCheckin } from "./EventTableComponents/DutyPeriodCheckin";
import { DutyPeriodCheckout } from "./EventTableComponents/DutyPeriodCheckout";
import { EventChip } from "src/components/Flights/EventChip";
import { NFAHotelSelector } from "./EventTableComponents/NFAHotelSelector";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import AlarmIcon from "@mui/icons-material/Alarm";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { IRootState } from "src/redux/reducers/CombinedReducers";
import { fetchRegLimitEvaluation } from "./EventTableComponents/FetchRegLimitEvaluation";

type IPairingEventTableProps = {
  id: string;
  editEvent: (type: string, editEvent: IPairingEvent, newEvent?: boolean) => void;
  selectedTableRow: (row: unknown) => void;
  /** Undefined when no logic modifier is selected. */
  selectedLM?: IPairingLogicModifier;
  updateLogicModifier: (logicModifier?: IPairingLogicModifier) => void;
  hotels: IPairingHotel[];
  updateHotels: (hotels: IPairingHotel[]) => void;
  restPeriodIndexDirty: number[];
  restHasError: (index: number) => boolean;
  rowHasError: (eventId: string) => boolean;
  displayAsUTC: boolean;
  setDisplayAsUTC: (value: boolean) => void;
  //new fields after parent form removal
  initPairing: IPairing;
  updateEventGroups: (eventGroups: IPairingEventGroup[]) => void;
  /** "Flight Duty Period Display" toggle, owned by the parent so it survives the editor's remount-on-save. */
  flightDutyPeriodDisplay: boolean;
  setFlightDutyPeriodDisplay: (value: boolean) => void;
}

export type IPairingTableRow = {
  _id: string;
  start: number | Date | undefined;
  end: number | Date | undefined;
  details: React.ReactNode;
  duration: number;
  type: string;
  color: string;
  event: IPairingEvent;
  displayAsUTC: boolean;
  startICAO?: string;
  endICAO?: string;
}

type IEventGroupTablesProps = {
  theme: Theme;
  pendingFlights?: IFlight[];
  eventGroupDisplay: IPairingEventGroup[];
  selectedRow: ISelectedRow;
  handlePopoverOpen: (event: React.MouseEvent<HTMLElement>, row?: IPairingTableRow) => void;
  storedPairing: IPairing | undefined;
  handleDelete: (index: number) => () => void;
  handleLogicModifierClick: (logicModifier: IPairingLogicModifier) => void;
  /** Undefined when no logic modifier is selected. */
  selectedLM?: IPairingLogicModifier;
  hotels: IPairingHotel[];
  restLocations: IRestHotelsLoaded[];
  updateHotels: (hotels: IPairingHotel[]) => void;
  restPeriodIndexDirty: number[];
  assignedCrew: IUser[] | undefined;
  restHasError: (index: number) => boolean;
  rowHasError: (eventId: string) => boolean;
  displayAsUTC: boolean;
  bulkRemoveMode: boolean;
  setEventGroupDisplay: React.Dispatch<React.SetStateAction<IPairingEditorEventGroup[]>>;
  checkedEvents: string[];
  setCheckedEvents: React.Dispatch<React.SetStateAction<string[]>>;
  updateEventGroups: (eventGroups: IPairingEventGroup[]) => void;
  id: string;
  flightDutyPeriodDisplay: boolean;
  regLimits: IRegLimitEvaluation | null;
  regLimitsLoading: boolean;
  regLimitsStale: boolean;
}

export type IPairingEditorEventGroup = {
  pendingFlights?: IFlight[];
  newFlights?: IFlight[];
  sortedEvents?: IPairingTableRow[];
  expandedState?: boolean;
  setExpandedStates?: unknown;
} & IPairingEventGroup

type IRestHotelsLoaded = {
  location: string;
  hotelOptions: IHotel[];
}

export type IPairingEvent = IFlight | IPositioningFlight | INonFlyingAssignment | IPairingRest | IGroundEvent;

const OCPairingEventTable = styled(Box)(({ theme }) => ({
  padding: "10px",
  "& .main_time_mode_selector": {
    width: "auto",
    marginBottom: "10px",
    // Keep each toggle's label and switch on a single line (don't wrap inside a fixed width).
    "& .time_mode_selector": { margin: 0, width: "auto", flexWrap: "nowrap" },
  },
  "& .event_group_container": {
    width: 840, // New width to better display menu options.
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: theme.custom.pairingDisplay.backgroundColor,
    padding: "4px 5px",
    borderRadius: "5px",
  },
  "&.dirty": {
    "& .table_title": {
      color: theme.custom.forms.dirtyColor,
      "& .edit_icon": {
        display: "inline-block",
        color: theme.custom.forms.dirtyColor,
        border: `1px solid ${theme.custom.forms.dirtyColor}`,
      },
    },
  },
  "& .table_title": {
    margin: "5px",
    fontSize: 16,
    color: theme.custom.forms.labels,
    "& .edit_icon": {
      display: "none",
      width: 16,
      height: 16,
      border: `1px solid ${theme.custom.forms.labels}`,
      borderRadius: "3px",
      margin: "0px 5px",
      verticalAlign: "text-top",
    },
  },
  "& .rest_period_summary": {
    padding: "10px 5px",
    color: theme.custom.forms.inputText,
    fontSize: 12,
    display: "flex",
    flexDirection: "column",
    fontWeight: 300,
    "&.on_duty_rest": {
      flexDirection: "initial",
      paddingLeft: "unset",
      alignItems: "center",
    },
    "&.dirty": {
      color: theme.custom.forms.dirtyColor,
    },
    "&.error": {
      backgroundColor: alpha(theme.palette.error.main, 0.5),
    },
    "&.discontinuity": {
      color: theme.custom.forms.errorColor,
    },
    "& .title": {
      margin: "0px 8px",
      minWidth: 100,
      color: theme.palette.primary.contrastText,
      textTransform: "uppercase",
      fontWeight: 300,
    },
    "& .rest_hotel_selector": {
      width: 300,
      margin: "0px 5px",
      "& .input_control label": { width: "auto" },
      "& .input_control .react-select-container .react-select__control": {
        "& .react-select__value-container": { padding: "0px 2px" },
      },
      // WHEN DIRTY
      "&.dirty": {
        "& .input_control .react-select-container .react-select__control": {
          "& .react-select__value-container": {
            "& .react-select__single-value": { color: theme.custom.forms.dirtyColor },
          },
        },
      },
      // ON HOVER
      "&:hover": {
        "& .input_control .react-select-container .react-select__control": {
          borderColor: alpha(theme.custom.forms.inputBorder, 1),
        },
      },
    },
  },
  "& .collapsible_eventgroup": {
    backgroundColor: theme.custom.timeline.contextListBackground,
    borderRadius: "3px",
    margin: 0,
    "&:not(:last-child)": { margin: "0px 0px 5px 0px " },
    "&:before": { display: "none" },
    "& .duty_period_summary": {
      color: theme.custom.forms.inputText,
      fontSize: 12,
      fontWeight: 300,
      "& .datetimefield": {
        display: "inline-flex",
        width: "auto",
        margin: "0px 5px",
        "& .input_control label": { display: "none" },
        "& .input_control .time-input-container": { margin: 0 },
      },
      "& .override_warning": { color: theme.palette.error.main },
      "& .checkboxfield": {
        display: "inline-flex",
        margin: "0px 5px",
        verticalAlign: "middle",
        width: "auto",
        "& label .label": {
          margin: "0px 5px 2px 0px",
          color: theme.custom.forms.inputText,
        },
        "&.checked": {
          "& label .label": { color: "yellow" }, // #ffd351
          "& svg path": { fill: "yellow " }, // #ffd351
        },
      },
      "& .title": {
        margin: "0px 5px",
        minWidth: 100,
        color: theme.palette.primary.contrastText,
        textTransform: "uppercase",
        fontWeight: 300,
      },
    },
    "& .MuiTableCell-root": {
      padding: 0,
      minWidth: 105,
      textAlign: "center",
    },
    "& .actions_column": {
      width: 24,
      padding: 0,
      "& button": { minWidth: 24, padding: 0 },
    },
    "& .type_column": {
      width: 105,
      fontSize: 12,
      padding: "0px 2px",
    },
    "& .start_column": { minWidth: 130, fontSize: 12 },
    "& .end_column": { minWidth: 130, fontSize: 12 },
    "& .details_column": {
      minWidth: 80,
      fontSize: 12,
      "& .flight_details": {
        display: "flex",
        flexWrap: "wrap",
        "& div": { margin: "0px 5px 0px 0px" },
      },
    },
    "& .duration_column": { minWidth: 50, fontSize: 12 },

    "& .collapsible_eventgroup_details": {
      padding: "5px",
      "& .eventgroup_table_header": {
        "& .MuiTableCell-head": {
          padding: "5px 10px",
          lineHeight: "1em",
          margin: 0,
        },
      },
      "& .eventgroup_table_body": {
        color: theme.custom.tables.contentColor,
        "& .table_row": {
          "&.error": {
            backgroundColor: alpha(theme.palette.error.dark, 0.5),
          },
          cursor: "default",
          "&.position_overrides": {
            borderBottom: `1px solid ${theme.custom.pairingDisplay.backgroundColor}`,
            "& .position_override_control": {
              "& .position_override_arrow": {
                width: 120,
                fontSize: 12,
                margin: "5px 0px",
                "& .icon": {
                  transform: "rotate(48deg)",
                  width: 15,
                  height: 15,
                  margin: "0px 5px",
                },
              },
              "& .selectfield.position_override": {
                width: "auto",
                "& .input_control": {
                  flexDirection: "row-reverse",
                  "& .react-select-container": { minWidth: 50 },
                  "& label": {
                    width: "auto",
                    color: "white",
                    margin: "5px",
                  },
                  "& .react-select__control": {
                    minWidth: 40,
                    backgroundColor: alpha("#000000", 0.5),
                    "& .react-select__value-container": {
                      padding: 0,
                      "& .badge": {
                        height: 16,
                        width: 20,
                        padding: 0,
                        lineHeight: "13px",
                      },
                    },
                  },
                },
              },
              "& .crew_position_display": { margin: "5px" },
              "& .remove_override_btn": {
                margin: "5px",
                padding: "2px 8px",
                fontSize: 12,
              },
            },
          },
          "& .MuiTableCell-root": {
            color: theme.custom.tables.contentColor,
            border: "none",
          },
          "& .duration_column .layout": {
            float: "right",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .duration_value": { marginRight: "40px" },
            "& .menu_btn": {
              margin: "0px 5px 0px 10px",
              padding: 0,
              borderRadius: "3px",
              display: "none",
            },
          },
          "&:hover, &.Mui-selected": {
            "& .MuiTableCell-root": {
              color: theme.palette.primary.contrastText,
            },
            "& .duration_column .layout .duration_value": { marginRight: 0 },
            "& .duration_column .layout .menu_btn": { display: "block" },
          },
        },
        "& .table_row.pendingFlight": {
          opacity: 0.5,
        },
      },
    },
  },
}));

// Extracted EventGroupTables component
const EventGroupTables = (props: IEventGroupTablesProps): JSX.Element => {
  const {
    theme,
    pendingFlights,
    eventGroupDisplay,
    selectedRow,
    handlePopoverOpen,
    storedPairing,
    handleDelete,
    handleLogicModifierClick,
    selectedLM,
    hotels,
    restLocations,
    updateHotels,
    restPeriodIndexDirty,
    assignedCrew,
    restHasError,
    rowHasError,
    displayAsUTC,
    bulkRemoveMode,
    setEventGroupDisplay,
    checkedEvents,
    setCheckedEvents,
    updateEventGroups,
    id,
    flightDutyPeriodDisplay,
    regLimits,
    regLimitsLoading,
    regLimitsStale,
  } = props; 

  return eventGroupDisplay.length > 0 || (pendingFlights && pendingFlights.length > 0) ? (
    <div className="event_group_container">
      {createPairingTable(
        pendingFlights ?? [],
        eventGroupDisplay,
        selectedRow,
        handlePopoverOpen,
        {
          logicModifiers: storedPairing?.logicModifiers ?? [],
          handleDelete,
          handleLogicModifierClick,
          activeSelection: selectedLM,
        },
        hotels,
        restLocations,
        updateHotels,
        restPeriodIndexDirty,
        assignedCrew,
        restHasError,
        rowHasError,
        displayAsUTC,
        theme,
        bulkRemoveMode,
        setEventGroupDisplay,
        checkedEvents,
        setCheckedEvents,
        updateEventGroups,
        id,
        storedPairing,
        flightDutyPeriodDisplay,
        regLimits,
        regLimitsLoading,
        regLimitsStale
      )}
    </div>
  ) : (
    <></>
  );
};

const eventMenuStyles = (theme: Theme): object => ({
  boxShadow: theme.shadows[1],
  borderRadius: "3px",
  backgroundColor: alpha(theme.palette.common.black, 0.85),
  "& .event_popover_menu": {
    padding: 0,
    "& li": {
      fontSize: 12,
      "&:hover": { color: theme.palette.primary.contrastText },
    },
  },
});

// PAIRING FLIGHTS
const pairingFlightsTableDesc = {
  tableTitle: "Pairing Id",
  // tableDescription: "To un-assign a flight,  use the filter drop down to find the desired events. Once found, select from the drop-down menu to unassign your flight.",
  tableDescription: "",
  noMessage: "No assigned flights to display",
};
const pairingFlightsTableInfo: ITableColumn[] = [
  {
    id: "_id",
    value: "_id",
    index: 0,
    numeric: true,
    label: "FlightChip",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "origin",
    value: "origin",
    index: 1,
    numeric: true,
    label: "Origin",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "destination",
    value: "destination",
    index: 2,
    numeric: true,
    label: "Destination",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "actualOut",
    value: "actualOut",
    index: 3,
    numeric: true,
    label: "Out Time",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "actualIn",
    value: "actualIn",
    index: 4,
    numeric: true,
    label: "In Time",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
];

export const defaultEventGroup: IPairingEditorEventGroup = {
  flights: [],
  nonFlyingAssignments: [],
  positioningFlights: [],
  groundEvents: [],
  restPeriods: [],
  // temporal event
  start: 0,
  end: 0,
  origin: "",
  originTimezoneId: "",
  originCountry: "",
  destination: "",
  destinationTimezoneId: "",
  destinationCountry: "",
  logicModifiers: [],
};

type IPairingTableData = {
  id: string;
  dataType: string;
  label: string;
  data: IPairingTableRow[];
  tableInfo: ITableColumn[];
  tableInfoDisplay: typeof pairingFlightsTableDesc;
};

// Function used to create the dataSet needed for the table component.
// This particular dataSet contains an array with all the available data types for the advanced search functionality.
const createTableData = (pairingEvents: IPairingTableRow[]): IPairingTableData => {
  return {
    id: "pairing_event",
    dataType: "pairing_event",
    label: "Pairing",
    data: pairingEvents,
    tableInfo: pairingFlightsTableInfo,
    tableInfoDisplay: pairingFlightsTableDesc,
  };
};

// Order table rows by their start time. Rows carry a `number | Date` start (or none), so compare by epoch value.
const sortByStart = (a: IPairingTableRow, b: IPairingTableRow): number => {
  const aStart = (a.start ?? 0).valueOf();
  const bStart = (b.start ?? 0).valueOf();
  return aStart < bStart ? -1 : aStart > bStart ? 1 : 0;
};

const renderEventChips = (type: string, data: unknown): JSX.Element => {
  if (data === null || data === undefined) {
    return <></>;
  }
  switch (type) {
    case "flight":
    case "pendingFlight":
    case "newFlight":
      return <FlightChip data={data as IFlight} />;
    case "nonFlight":
      return <NonFlightChip data={data} />;
    case "posFlight":
      return <PosFlightChip data={data as IPositioningFlight} />;
    case "groundEvent":
      return <EventChip data={data} />;
    default:
      return <></>;
  }
};

const renderCells = (
  row: IPairingTableRow,
  handlePopoverOpen: (event: React.MouseEvent<HTMLElement>, row?: IPairingTableRow) => void,
  theme: Theme,
  bulkRemoveMode: boolean,
  checkedEvents: string[],
  setCheckedEvents: React.Dispatch<React.SetStateAction<string[]>>
): JSX.Element[] => {
  const columnNames = Object.keys(row);
  return columnNames.map((key: string, idx: number) => {
    switch (key) {
      case "_id":
        return (
          <TableCell className="type_column color" scope="row" key={`row${key}${idx}`}>
            {row.type === "flight" ||
            row.type === "pendingFlight" ||
            row.type === "posFlight" ||
            row.type === "nonFlight" ||
            row.type === "groundEvent"
              ? renderEventChips(row.type, row.event)
              : "ZZzz.."}
          </TableCell>
        );
      case "start":
      case "end": {
        const cellValue = row[key];
        if (typeof cellValue === "number" && isEpochDate(cellValue)) {
          const tzICAO =
            !row.displayAsUTC && key === "start"
              ? row.startICAO
              : !row.displayAsUTC && key === "end"
                ? row.endICAO
                : undefined;
          let timeDiscrepancy = false;
          let tooltipDesc: React.ReactNode[] = [];
          if (
            key === "start" &&
            "actualOut" in row.event &&
            "scheduledOut" in row.event &&
            row.event.actualOut !== row.event.scheduledOut
          ) {
            timeDiscrepancy = true;
            tooltipDesc = [
              <b key={`label-1-idx-${idx}`}>Scheduled Out:</b>,
              <br key={`br-1-idx-${idx}`} />,
              <DateDisplay key={`date-1-idx-${idx}`} date={row.event.scheduledOut} tzICAO={tzICAO} />,
              <br key={`br-2-idx-${idx}`} />,
              <b key={`label-2-idx-${idx}`}>Actual Out:</b>,
              <br key={`br-3-idx-${idx}`} />,
              <DateDisplay key={`date-2-idx-${idx}`} date={row.event.actualOut ?? cellValue} tzICAO={tzICAO} />,
            ];
          }
          if (
            key === "end" &&
            "actualIn" in row.event &&
            "scheduledIn" in row.event &&
            row.event.actualIn !== row.event.scheduledIn
          ) {
            timeDiscrepancy = true;
            tooltipDesc = [
              <b key={`label-1-idx-${idx}`}>Scheduled In:</b>,
              <br key={`br-1-idx-${idx}`} />,
              <DateDisplay key={`date-1-idx-${idx}`} date={row.event.scheduledIn} tzICAO={tzICAO} />,
              <br key={`br-2-idx-${idx}`} />,
              <b key={`label-2-idx-${idx}`}>Actual In:</b>,
              <br key={`br-3-idx-${idx}`} />,
              <DateDisplay key={`date-2-idx-${idx}`} date={row.event.actualIn ?? cellValue} tzICAO={tzICAO} />,
            ];
          }
          return (
            <TableCell className={key === "start" ? "start_column" : "end_column"} scope="row" key={`row${key}${idx}`}>
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                sx={timeDiscrepancy ? { marginLeft: "11px" } : {}}
              >
                <DateDisplay date={cellValue} tzICAO={tzICAO} />
                {timeDiscrepancy && (
                  <Tooltip title={<span>{tooltipDesc}</span>}>
                    <AlarmIcon sx={{ color: theme.palette.warning.main, marginLeft: "3px" }} fontSize="small" />
                  </Tooltip>
                )}
              </Grid>
            </TableCell>
          );
        }
        return <Fragment key={`row${key}${idx}`}></Fragment>;
      }
      case "details":
        return (
          <TableCell className="details_column" scope="row" key={`row${key}${idx}`}>
            {row[key]}
          </TableCell>
        );
      case "duration": {
        const hours = Math.floor(row[key] / 3600); // need to be able to show 24 hours or greater
        const mins = Math.floor(row[key] / 60) % 60;

        const isChecked = (): boolean => {
          const id = row._id;
          if (checkedEvents.includes(id)) {return true;}
          return false;
        };

        const toggleCheck = (): void => {
          const tempCheckedEvents = [...checkedEvents];
          if (tempCheckedEvents.includes(row._id)) {
            tempCheckedEvents.splice(tempCheckedEvents.indexOf(row._id), 1);
          } else {
            tempCheckedEvents.push(row._id);
          }
          setCheckedEvents(tempCheckedEvents);
        };

        return (
          <TableCell className="duration_column" scope="row" key={`row_actions${idx}`}>
            <div className="layout">
              <div className={!bulkRemoveMode ? "duration_value" : ""}>{`${padNumber(hours, 2)}:${padNumber(
                mins,
                2
              )}`}</div>
              {!bulkRemoveMode && (
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLElement>) => handlePopoverOpen(event, row)}
                  className="menu_btn"
                  id="edit_pairing_event_menu_btn"
                  aria-label="edit pairing event"
                  size="medium"
                  sx={{ fontSize: "0.8em" }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              {bulkRemoveMode && (
                <Checkbox
                  checked={isChecked()}
                  onChange={toggleCheck}
                  sx={{ pt: 0, pb: 0, "& .MuiSvgIcon-root": { fontSize: 14 } }}
                />
              )}
            </div>
          </TableCell>
        );
      }
      default:
        return <Fragment key={`row${key}${idx}`}></Fragment>;
    }
  });
};

const renderRows = (
  id: string,
  rows: IPairingTableRow[],
  selectedRow: IPairingTableRow | null,
  handlePopoverOpen: (event: React.MouseEvent<HTMLElement>, row?: IPairingTableRow) => void,
  rowHasError: (eventId: string) => boolean,
  theme: Theme,
  bulkRemoveMode: boolean,
  checkedEvents: string[],
  setCheckedEvents: React.Dispatch<React.SetStateAction<string[]>>,
  updateEventGroups: (eventGroups: IPairingEventGroup[]) => void
): JSX.Element[] => {
  if (rows.length === 0) {
    return [<Fragment key={0}></Fragment>]; // Since there is only 1 fragment, the index (key) will always be 0.
  }
  return rows
    .sort(sortByStart)
    .map((row) => {
      const isSelected = selectedRow?._id != null && selectedRow._id !== "" && selectedRow._id === row._id;

      const flightIsRemoved: boolean =
        (row.type === "flight" || row.type === "posFlight") &&
        "removalType" in row.event &&
        row.event.removalType != null;

      return (
        <React.Fragment key={row._id}>
          <TableRow
            className={`table_row ${row.type} ${(rowHasError(row._id) || flightIsRemoved) && "error"}`}
            hover
            key={`event_${row._id}`}
            // onContextMenu={handlePopoverOpen}
            tabIndex={-1}
            selected={isSelected}
          >
            {renderCells(row, handlePopoverOpen, theme, bulkRemoveMode, checkedEvents, setCheckedEvents)}
          </TableRow>
          {row.type === "nonFlight" &&
            "assignmentType" in row.event &&
            row.event.assignmentType.isOnDutyRest && (
              <NFAHotelSelector id={id} nfa={row.event} updateEventGroups={updateEventGroups} />
            )}
          <FlightPositionOverride id={id} flightId={row._id} active={isSelected} />
        </React.Fragment>
      );
    });
};

// Returns a formatted flight row dataset.
const returnFlightRow = (flights: IFlight[], flightType: string, displayAsUTC: boolean): IPairingTableRow[] => {
  return flights.length > 0
    ? flights.map((flight: IFlight): IPairingTableRow => {
        const aircraftInfo = flight.aircraft?.aircraftType ? (
          <div className="flight_details">
            <div>{flight.aircraft.registration}</div>
            <div>{`(${flight.aircraft.aircraftType.title})`}</div>
          </div>
        ) : null;
        return {
          _id: flight._id,
          start: flight.actualOut,
          end: flight.actualIn,
          details: aircraftInfo ?? "none available.",
          duration: Math.abs((flight.actualIn?.valueOf() ?? 0) - (flight.actualOut?.valueOf() ?? 0)) / 1000,
          type: flightType,
          color: !!flight.flightType.color ? LightenDarkenColor(flight.flightType.color, -0.5) : "none",
          event: flight,
          // local switch additions
          displayAsUTC: displayAsUTC,
          startICAO: flight.origin,
          endICAO: flight.destination,
        };
      })
    : [];
};

const renderRestSummary = (
  eventGroup: IPairingEditorEventGroup,
  hotels: IPairingHotel[],
  hotelOptions: IHotel[],
  updateHotels: (hotels: IPairingHotel[]) => void,
  restPeriodIndexDirty: number[],
  logicModifiersProps: ILMGroupChipProps,
  restHasError: (index: number) => boolean
): JSX.Element => {
  const sortHotelsByPriority = (a: IHotel, b: IHotel): number => {
    return b.priority > a.priority ? -1 : 1;
  };
  if (eventGroup.restPeriods.length === 0) {return <></>;}
  const { start, end, origin, destination } = eventGroup.restPeriods[0];
  // Filter hotel options and show only those available for this icao.
  const availableHotels = origin ? hotelOptions.filter((h) => h.icao === origin).sort(sortHotelsByPriority) : [];
  const groupIndex = eventGroup.restPeriods[0].eventGroup ?? -1;
  const hotelsDirty = restPeriodIndexDirty.includes(groupIndex);
  // hotels associated with rest period (restIndex must match group index).
  const selectedHotel = hotels.find((hotel) => hotel.restIndex === groupIndex);

  const hasError = restHasError(groupIndex);

  const handleUpdateHotels = (newHotel: IHotel | null): void => {
    // Deep clone needs to be made here as to not mutate the initial values.
    const newHotels = _.cloneDeep(hotels);
    const modifyIndex = newHotels.findIndex((hotel) => hotel.restIndex === groupIndex);
    if (newHotel != null && modifyIndex > -1 && newHotel._id !== selectedHotel?.hotel?._id) {
      // UPDATE HOTEL
      newHotels[modifyIndex].hotel = newHotel;
    } else if (newHotel != null && modifyIndex === -1) {
      // ADD NEW
      newHotels.push({
        hotel: newHotel,
        restIndex: groupIndex,
      });
    } else if (newHotel == null) {
      // CLEAR HOTEL
      newHotels[modifyIndex].hotel = newHotel;
      // newHotels.splice(modifyIndex, 1);
    }
    updateHotels(newHotels);
  };

  const logicModifierInRange = (logicModifiers: IPairingLogicModifier): boolean =>
    logicModifiers.dutyTime != null &&
    logicModifiers.dutyTime >= start.valueOf() &&
    logicModifiers.dutyTime <= end.valueOf();

  const logicModifiersInRest = logicModifiersProps.logicModifiers.filter(
    (lms: IPairingLogicModifier) =>
      lms.dutyTime != null && lms.dutyTime >= start.valueOf() && lms.dutyTime <= end.valueOf()
  );

  return (
    <div
      className={`rest_period_summary ${hotelsDirty && "dirty"} ${hasError && "error"} ${
        origin !== destination && "discontinuity"
      }`}
    >
      <Grid container alignItems="center">
        <Grid item style={{ fontWeight: 500, marginRight: 5 }}>
          Rest
        </Grid>
        <Grid item className="rest_duration" style={{ marginRight: 5 }}>
          {`${convertMilli2DaysHrsMin(end.valueOf() - start.valueOf())} `}
        </Grid>
        {
          <>
            at
            <SelectField
              className={`rest_hotel_selector ${hotelsDirty && "dirty"}`}
              label=""
              name={`rest_${groupIndex}_hotel`}
              getOptionLabel={(option: IHotel) =>
                `${option.name} ${option.phoneNumber != null && option.phoneNumber !== "" ? ` (${option.phoneNumber})` : ""}`
              }
              getOptionValue={(option: IHotel) => option._id ?? ""}
              closeMenuOnSelect={true}
              hideSelectedOptions={false}
              options={availableHotels}
              onChange={handleUpdateHotels}
              value={selectedHotel?.hotel}
              isClearable={true}
              // menuPortalTarget={parentElement}
              placeholder="Select Hotel..."
              // closeMenuOnScroll={true} Only works on window scroll so basically useless.
              menuPosition="fixed"
              maxMenuHeight={400}
              disabled={origin !== destination}
            />
          </>
        }
        <Grid item className={"rest_origin "}>{`in ${
          origin === destination ? origin : `${origin}/${destination}`
        }`}</Grid>
      </Grid>
      {logicModifiersInRest.length > 0 && <LMGroupChip {...{ ...logicModifiersProps, logicModifierInRange }} />}
    </div>
  );
};

type IBookingIdent = {
  _id?: string;
  locator?: string;
  crew?: string[];
}

const displayBookings = (bookings: IBookingIdent[], tempPairing: IPairing): JSX.Element => (
  <LocatorChips bookings={bookings} pairing={tempPairing} />
);

const createPairingTable = (
  hoveringFlights: IFlight[],
  eventGroups: IPairingEditorEventGroup[],
  selectedRow: ISelectedRow,
  handlePopoverOpen: (event: React.MouseEvent<HTMLElement>, row?: IPairingTableRow) => void,
  logicModifiersProps: ILMGroupChipProps,
  hotels: IPairingHotel[],
  restLocations: IRestHotelsLoaded[],
  updateHotels: (hotels: IPairingHotel[]) => void,
  restPeriodIndexDirty: number[],
  assignedCrew: IUser[] | undefined,
  restHasError: (index: number) => boolean,
  rowHasError: (eventId: string) => boolean,
  displayAsUTC: boolean,
  theme: Theme,
  bulkRemoveMode: boolean,
  setEventGroupDisplay: React.Dispatch<React.SetStateAction<IPairingEditorEventGroup[]>>,
  checkedEvents: string[],
  setCheckedEvents: React.Dispatch<React.SetStateAction<string[]>>,
  updateEventGroups: (eventGroups: IPairingEventGroup[]) => void,
  id: string,
  storedPairing: IPairing | undefined,
  flightDutyPeriodDisplay: boolean,
  regLimits: IRegLimitEvaluation | null,
  regLimitsLoading: boolean,
  regLimitsStale: boolean
): JSX.Element[] => {
  let eventGroupArray: IPairingEditorEventGroup[] = [];

  const { logicModifiers } = logicModifiersProps;
  if (eventGroups.length === 0 && hoveringFlights.length > 0) {
    const pendingFlights = returnFlightRow(hoveringFlights, "pendingFlight", displayAsUTC);
    const eventGroup: IPairingEditorEventGroup = {
      ...defaultEventGroup,
      sortedEvents: [...pendingFlights].sort(sortByStart),
    };
    eventGroupArray = [eventGroup];
  } else {
    eventGroupArray = eventGroups.map((group: IPairingEditorEventGroup) => {
      let eventArray: IPairingTableRow[] = [];
      // const pendingFlights = group.pendingFlights ? returnFlightRow(group.pendingFlights, "pendingFlight") : [];
      const pendingFlights = returnFlightRow(hoveringFlights, "pendingFlight", displayAsUTC);
      // const newFlights = group.newFlights ? returnFlightRow(group.newFlights, "newFlight") : [];
      const flights = returnFlightRow(group.flights, "flight", displayAsUTC);
      // const restPeriods = group.restPeriods ? returnRestRow(group.restPeriods, "rest", restHotel) : [];

      const positioningFlights =
        group.positioningFlights.length > 0
          ? group.positioningFlights.map((posFlight: IPositioningFlight): IPairingTableRow => {
              // Append assigned crew onto posFlight
              const posFlightWithCrew = { ...posFlight, crew: assignedCrew };
              const aircraftType = posFlight.aircraft?.aircraftType;
              const aircraftInfo = aircraftType ? (
                <div className="flight_details">
                  <div>{posFlight.aircraft?.registration}</div>
                  <div>{aircraftType.title !== "" ? `(${aircraftType.title})` : ""}</div>
                </div>
              ) : null;
              return {
                _id: posFlight._id ?? posFlightWithCrew._tempId, // If no idea is present, map temp id if available
                start: posFlight.outTime.valueOf(),
                end: posFlight.inTime.valueOf(),
                details:
                  posFlight.bookings.length > 0 && storedPairing != null
                    ? displayBookings(posFlight.bookings, storedPairing)
                    : posFlight.requiresBooking === true
                      ? "bookings required"
                      : (aircraftInfo ?? null),
                duration: Math.abs(posFlight.inTime.valueOf() - posFlight.outTime.valueOf()) / 1000,
                type: "posFlight",
                color: LightenDarkenColor("ff00ff", -0.5),
                event: posFlightWithCrew,
                // local switch additions
                displayAsUTC: displayAsUTC,
                startICAO: posFlight.origin,
                endICAO: posFlight.destination,
              };
            })
          : [];

      const nonFlyingAssignments =
        group.nonFlyingAssignments.length > 0
          ? group.nonFlyingAssignments.map((nonFlight: INonFlyingAssignment): IPairingTableRow => {
              return {
                _id: nonFlight._id ?? nonFlight._tempId, // If no idea is present, map temp id if available,
                start: nonFlight.start.valueOf(),
                end: nonFlight.end.valueOf(),
                details: nonFlight.assignmentType.description
                  ? nonFlight.assignmentType.description
                  : "Non-Flying Event",
                duration: Math.abs(nonFlight.end.valueOf() - nonFlight.start.valueOf()) / 1000,
                type: "nonFlight",
                color: nonFlight.assignmentType.defaultColor
                  ? LightenDarkenColor(nonFlight.assignmentType.defaultColor, -0.5)
                  : "none",
                event: nonFlight,
                // local switch additions
                displayAsUTC: displayAsUTC,
                startICAO: nonFlight.locationICAO,
                endICAO: nonFlight.locationICAO,
              };
            })
          : [];

      const groundEvents =
        group.groundEvents.length > 0
          ? group.groundEvents.map((groundEvent: IGroundEvent): IPairingTableRow => {
              const aircraftType = groundEvent.aircraft?.aircraftType;
              const aircraftInfo = aircraftType ? (
                <div className="flight_details">
                  <div>{groundEvent.aircraft?.registration}</div>
                  <div>{`(${aircraftType.title ?? ""})`}</div>
                </div>
              ) : null;
              return {
                _id: groundEvent._id,
                start: groundEvent.actualStart,
                end: groundEvent.actualEnd,
                details: aircraftInfo,
                duration: Math.abs(groundEvent.actualEnd.valueOf() - groundEvent.actualStart.valueOf()) / 1000,
                type: "groundEvent",
                color: groundEvent.eventType.color ? LightenDarkenColor(groundEvent.eventType.color, -0.5) : "none",
                event: groundEvent,
                // local switch additions
                displayAsUTC: displayAsUTC,
                startICAO: groundEvent.location,
                endICAO: groundEvent.location,
              };
            })
          : [];
      // Spread out all the events into a group array.
      eventArray = [...eventArray, ...pendingFlights];
      // eventArray = [...eventArray, ...newFlights];
      eventArray = [...eventArray, ...flights];
      eventArray = [...eventArray, ...groundEvents];
      //make sure all non new events are unique - pos flights and nfas may not have a _id field
      eventArray = _.uniqBy(eventArray, _.iteratee("_id"));
      // eventArray = [...eventArray, ...restPeriods];
      eventArray = [...eventArray, ...positioningFlights];
      eventArray = [...eventArray, ...nonFlyingAssignments];
      // Add sorted events to group.
      group.sortedEvents = eventArray.sort(sortByStart);
      // const [firstEventTime, lastEventTime] =   [group.sortedEvents[0]["start"], group.sortedEvents.slice(-1).pop()["end"]];
      group.logicModifiers = logicModifiers.filter(
        (lm: IPairingLogicModifier) =>
          lm.dutyTime != null && lm.dutyTime >= group.start.valueOf() && lm.dutyTime <= group.end.valueOf()
      );
      return group;
    });
  }
  /**
   * Returns the Event Group Table with collapsible duty periods.
   */
  // Counter into the reg-limit `dutyGroups` array, which contains duty periods only (no rest periods),
  // so it must advance independently of the eventGroupArray index.
  let dutyPeriodIndex = -1;
  return eventGroupArray.map((eventGroup: IPairingEditorEventGroup, index: number) => {
    const isRestPeriod = eventGroup.restPeriods.length > 0;
    if (isRestPeriod) {
      const restLocation = restLocations.find((rest) => rest.location === eventGroup.origin);
      const hotelOptions = restLocation ? restLocation.hotelOptions : [];
      return (
        <div key={`eventGroup-${index}`}>
          {renderRestSummary(
            eventGroup,
            hotels,
            hotelOptions,
            updateHotels,
            restPeriodIndexDirty,
            logicModifiersProps,
            restHasError
          )}
        </div>
      );
    } else {
      dutyPeriodIndex += 1;
      const tableData = createTableData(eventGroup.sortedEvents ?? []);
      return (
        <Accordion
          square
          expanded={eventGroup.expandedState}
          className="collapsible_eventgroup"
          //id={`eventgroup-${_id}`}
          key={`eventGroup-${index}`}
        >
          <AccordionSummary
            sx={{ cursor: "default !important" }}
            expandIcon={
              <ExpandMoreIcon
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  const tempGroups = [...eventGroups];
                  tempGroups[index].expandedState = tempGroups[index].expandedState !== true;
                  setEventGroupDisplay(tempGroups);
                }}
              />
            }
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <DutyPeriodSummary
              id={id}
              eventGroupIndex={index}
              eventGroup={eventGroup}
              logicModifiersProps={logicModifiersProps}
              bulkRemoveMode={bulkRemoveMode}
              checkedEvents={checkedEvents}
              setCheckedEvents={setCheckedEvents}
              flightDutyPeriodDisplay={flightDutyPeriodDisplay}
              regLimits={regLimits}
              regLimitsLoading={regLimitsLoading}
              dutyPeriodIndex={dutyPeriodIndex}
              regLimitsStale={regLimitsStale}
            />
          </AccordionSummary>

          <AccordionDetails style={{ padding: "2px 8px", display: "inherit" }}>
            <DutyPeriodCheckin id={id} eventGroupIndex={index} eventGroup={eventGroup} displayAsUTC={displayAsUTC} />
          </AccordionDetails>

          <AccordionDetails className="collapsible_eventgroup_details">
            <Table className="teventgroup_table_content">
              <TableHead className="eventgroup_table_header">
                <TableRow>
                  <TableCell className="type_column">Type</TableCell>
                  <TableCell className="start_column">Flight Out / Start</TableCell>
                  <TableCell className="end_column">Flight In / End</TableCell>
                  <TableCell className="details_column">Details</TableCell>
                  <TableCell className="duration_column">Duration (hrs:min)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="eventgroup_table_body">
                {renderRows(
                  id,
                  tableData.data,
                  selectedRow.row,
                  handlePopoverOpen,
                  rowHasError,
                  theme,
                  bulkRemoveMode,
                  checkedEvents,
                  setCheckedEvents,
                  updateEventGroups
                )}
              </TableBody>
            </Table>
          </AccordionDetails>

          <AccordionDetails style={{ padding: "2px 8px" }}>
            <DutyPeriodCheckout id={id} eventGroupIndex={index} eventGroup={eventGroup} displayAsUTC={displayAsUTC} />
          </AccordionDetails>
        </Accordion>
      );
    }
  });
};

type ISelectedRow = {
  anchorEl: HTMLElement | null;
  row: IPairingTableRow | null;
}

///////////////////////////////////////////
///////////  EVENTGROUPS  FORM ////////////
///////////////////////////////////////////
export const PairingEventTable = (props: IPairingEventTableProps): JSX.Element => {
  const {
    id,
    // fieldProps,
    editEvent,
    selectedTableRow,
    selectedLM,
    updateLogicModifier,
    hotels,
    updateHotels,
    restPeriodIndexDirty,
    restHasError,
    rowHasError,
    displayAsUTC,
    setDisplayAsUTC,
    initPairing,
    updateEventGroups,
    flightDutyPeriodDisplay,
    setFlightDutyPeriodDisplay,
  } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  // `pairingEditors` is an index-signature map, so `[id]` is `IPairing | undefined` at runtime even though the
  // (looser) declared type omits the `undefined`. Annotating it here tells the truth, which is what makes the
  // `storedPairing?.` guards throughout this file legitimate rather than "unnecessary".
  const storedPairing = useSelector(
    (state: IRootState): IPairing | undefined => state.crewSchedule.pairingEditors[id]
  );
  const value = useMemo(
    () => (storedPairing?.eventGroups ?? []) as IPairingEditorEventGroup[],
    [storedPairing?.eventGroups]
  );
  const initialValue = useMemo(() => initPairing.eventGroups as IPairingEditorEventGroup[], [initPairing.eventGroups]);
  const [touched, setTouched] = useState<boolean>(false);

  const [selectedRow, setSelectedRow] = useState<ISelectedRow>({ anchorEl: null, row: null });
  const [bulkRemoveMode, setBulkRemoveMode] = useState<boolean>(false);
  // Regulation-limit evaluation for the pairing. Fetched on demand when the toggle is ON
  // Note the toggle state is managed by parent, to survive save/refresh
  const [regLimits, setRegLimits] = useState<IRegLimitEvaluation | null>(null);
  const [regLimitsLoading, setRegLimitsLoading] = useState<boolean>(false);
  const [regLimitsPairing, setRegLimitsPairing] = useState<IPairing | null>(null);
  // Store separate eventGroupDisplay array (used to show pending flights dragged but not dropped onto pairing)
  const [eventGroupDisplay, setEventGroupDisplay] = useState<IPairingEditorEventGroup[]>([]);
  const [checkedEvents, setCheckedEvents] = useState<string[]>([]);
  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLElement>, row?: IPairingTableRow): void => {
    event.stopPropagation();
    event.preventDefault();
    if (row?._id != null && row._id !== "") {
      setSelectedRow({ anchorEl: event.currentTarget, row }); // Select active row menu was opened on.
    }
  }, []);

  // Store available hotel options based on Rest origins
  const [restLocations, setRestLocations] = useState<IRestHotelsLoaded[]>([]);

  useEffect(() => {
    if (value.length === 0) {
      return;
    }

    const getHotelOptions = async (icao: string): Promise<IHotel[]> => {
      const newHotelOptions = await AmplifyAPIWrapper.Get<IHotel[]>(`/hotels?icao=${icao}`);
      return Array.isArray(newHotelOptions) && newHotelOptions.length > 0 ? newHotelOptions : [];
    };

    const newPromises = async (): Promise<IRestHotelsLoaded[]> => {
      const newDataArray = (value as IPairingEventGroup[]).reduce(
        async (acc: Promise<IRestHotelsLoaded[]>, group: IPairingEventGroup) => {
          const accumulator = await acc;
          const origin = group.restPeriods.at(0)?.origin;
          // If rest location/hotels don't yet exist, fetch hotel options and add.
          if (origin != null && origin !== "" && !accumulator.find((u) => u.location === origin)) {
            const response = await getHotelOptions(origin);
            const newRest = {
              location: origin,
              hotelOptions: Array.isArray(response) ? response : [],
            };
            accumulator.push(newRest);
          }
          return Promise.resolve(accumulator);
        },
        Promise.resolve([])
      );
      return newDataArray;
    };

    // Reduce rest locations and get hotel options for any that don't yet have them.
    // Once this is done, update state value.
    void newPromises().then((newRestLocs) => {
      setRestLocations(newRestLocs);
    });
  }, [value]);

  const handlePopoverClose = useCallback((): void => {
    setSelectedRow((prev) => ({ ...prev, anchorEl: null }));
  }, []);

  // Return true if both initialValue and value are populated, different and field has been touched.
  // I added this because for some odd reason, Formik doesn't provide one in the meta object.
  const EventGroupsDirty: boolean = useMemo(() => {
    return !_.isEqual(initialValue, value) && touched;
  }, [initialValue, value, touched]);

  // True when the displayed reg-limit evaluation no longer matches the live editor state - i.e. the pairing has
  // been edited since the limits were last fetched (either the saved-baseline effect or the manual recalculate).
  // This drives the greyed-out "Save to refresh" chip; recalculating for the dirty pairing clears it.
  const regLimitsStale: boolean = useMemo(() => {
    return !!regLimits && !!regLimitsPairing && !_.isEqual(storedPairing, regLimitsPairing);
  }, [regLimits, regLimitsPairing, storedPairing]);

  useEffect(() => {
    // Pass selected row to parent controllers.
    selectedTableRow(selectedRow.row);
  }, [selectedTableRow, selectedRow.row]);

  useEffect(() => {
    // Make sure this is a clone as we don't want the display object mutating the values.
    const groupDisplay = _.cloneDeep(value);
    for (let n = 0; n < groupDisplay.length; n++) {
      groupDisplay[n].expandedState = true;
    }
    setEventGroupDisplay(groupDisplay);
  }, [storedPairing, value]);

  /**
   * Display pairing event actions menu.
   */
  const eventMenu = useMemo((): JSX.Element => {
    const row = selectedRow.row;
    const anchorEl = selectedRow.anchorEl;
    const open = Boolean(anchorEl);

    // Return null for the menu if no row is selected (nothing to act on).
    if (row == null) {
      return <></>;
    }
    const type = row.type;
    const event = row.event;

    const typeString: string =
      type === "posFlight"
        ? "Position Flight"
        : type === "nonFlight"
          ? "Non-flying Assignment"
          : type === "groundEvent"
            ? "Ground Event"
            : type;

    const handleEditEvent = (actionType: string, actionEvent: IPairingEvent, isNew?: boolean): void => {
      switch (actionType) {
        case "flight": // Open flight editor
          dispatch(openSelectedEventWindow(actionEvent as IFlight, []));
          setSelectedRow({ anchorEl: null, row: null });
          break;
        case "posFlight":
        case "nonFlight":
          editEvent(actionType, actionEvent, isNew);
          break;
        case "groundEvent": //open groundevent editor
          dispatch(openSelectedEventWindow(actionEvent as IGroundEvent, []));
          setSelectedRow({ anchorEl: null, row: null });
          break;
        default:
          setSelectedRow({ anchorEl: null, row: null });
          break;
      }
    };

    // Shared function used in removal and converstion of items.
    const removeItem = (
      removeEvent: IPairingEvent,
      eventGroups: IPairingEditorEventGroup[]
    ): IPairingEditorEventGroup[] => {
      eventGroups.forEach((eventGroup: IPairingEditorEventGroup) => {
        switch (type) {
          case "flight":
            eventGroup.flights = eventGroup.flights.filter((flight: IFlight) => flight._id !== removeEvent._id);
            break;
          case "posFlight":
            eventGroup.positioningFlights = eventGroup.positioningFlights.filter((posFlight: IPositioningFlight) =>
              removeEvent._id != null
                ? posFlight._id !== removeEvent._id
                : posFlight._tempId !== (removeEvent as IPositioningFlight)._tempId
            );
            break;
          case "nonFlight":
            eventGroup.nonFlyingAssignments = eventGroup.nonFlyingAssignments.filter(
              (nonFlight: INonFlyingAssignment) =>
                removeEvent._id != null
                  ? nonFlight._id !== removeEvent._id
                  : nonFlight._tempId !== (removeEvent as INonFlyingAssignment)._tempId
            );
            break;
          case "groundEvent":
            eventGroup.groundEvents = eventGroup.groundEvents.filter(
              (groundEvent: IGroundEvent) => groundEvent._id !== removeEvent._id
            );
            break;
          default:
          // code block
        }
      });
      return eventGroups;
    };

    /**
     * Delete Flight button located on the left of each pairing flight.
     * @param deleteFlight - flight to delete
     */
    const handleRemoveEvent = (removeEvent: IPairingEvent): void => {
      const eventGroups = _.cloneDeep(value);
      const newEventGroups = removeItem(removeEvent, eventGroups);
      setSelectedRow({ anchorEl: null, row: null });
      updateEventGroups(newEventGroups);
    };

    // Convert flights to positioning flights and vice versa (if internal flights);
    // Uses convertFlightToPosFlight, convertPosFlightToFlight
    const eventCanBeConverted =
      typeString === "flight" ||
      ((typeString === "Position Flight" || typeString === "posFlight") &&
        (event as IPositioningFlight).flight_id != null);

    const handleConvertEvent = (actionType: string, convertEvent: IPairingEvent): void => {
      const eventGroups = _.cloneDeep(value);
      if (actionType === "flight") {
        // Add flight
        const posFlight = convertFlightToPosFlight(convertEvent as IFlight);
        eventGroups[0].positioningFlights.push(posFlight);
        // Remove positioning flight event...
        const newEventGroups = removeItem(convertEvent, eventGroups);
        setSelectedRow({ anchorEl: null, row: null });
        updateEventGroups(newEventGroups);
      } else {
        // add positioning flight
        const posFlight2Flight = async (): Promise<void> => {
          const flight = await convertPosFlightToFlight(convertEvent as IPositioningFlight);
          if (flight?._id != null) {
            eventGroups[0].flights.push(flight);
            // Remove flight...
            const newEventGroups = removeItem(convertEvent, eventGroups);
            setSelectedRow({ anchorEl: null, row: null });
            updateEventGroups(newEventGroups);
          }
        };
        void posFlight2Flight();
      }
    };

    // If no events are found with this id, event can be overriden.
    const CanBeOverriden =
      //DEV-148: If this doesn't work, try initPairing instead of tempPairing
      (type === "flight" || type === "groundEvent") &&
      storedPairing?.positionOverrides.find((override) => override.flightId === event._id) == null;

    //if there are no crew on pairing - disable the button
    const disableOverridePositionsButton =
      (storedPairing?.positions.findIndex((position) => position.crew != null) ?? -1) < 0;
    const handleOverridePositions = (overrideEvent: IPairingEvent): void => {
      const newPositionOverride: IPositionOverride = { flightId: overrideEvent._id, positions: [] };
      const updatedPositionOverrides = [...(storedPairing?.positionOverrides ?? []), newPositionOverride];
      dispatch(updatePairingEditor(id, { positionOverrides: updatedPositionOverrides }));
      setTouched(true);
    };

    const flightIsRemoved =
      (type === "flight" && (event as IFlight).removalType != null) ||
      (type === "posFlight" && (event as IPositioningFlight).removalType != null); // Show row in error state if flight is canceled;

    return (
      <Popover
        id={`${String(event._id ?? "empty")}-event_menu`}
        open={open}
        onClose={handlePopoverClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: eventMenuStyles(theme),
          },
        }}
      >
        {
          <div className="event_popover_menu">
            <MenuItem disabled={flightIsRemoved} onClick={() => handleEditEvent(type, event)}>
              Edit {typeString}
            </MenuItem>
            <MenuItem onClick={() => handleRemoveEvent(event)}>Remove {typeString}</MenuItem>
            {eventCanBeConverted && (
              <MenuItem disabled={flightIsRemoved} onClick={() => handleConvertEvent(type, event)}>
                {`Convert to ${
                  typeString === "posFlight" || typeString === "Position Flight" ? "Flight" : "Positioning Flight"
                }`}
              </MenuItem>
            )}
            {CanBeOverriden && (
              <MenuItem
                onClick={() => handleOverridePositions(event)}
                disabled={flightIsRemoved || disableOverridePositionsButton}
              >
                Override Crew Positions
              </MenuItem>
            )}
          </div>
        }
      </Popover>
    );
  }, [selectedRow, handlePopoverClose, dispatch, initPairing, editEvent, value, theme]);

  const handleDelete = useCallback(
    (index: number) => () => {
      const lms = storedPairing?.logicModifiers ? cloneDeep(storedPairing.logicModifiers) : [];
      lms.splice(index, 1);
      dispatch(updatePairingEditor(id, { logicModifiers: lms }));
    },
    [storedPairing]
  );

  /** Function toggle as selected LM when clicked */
  const handleLogicModifierClick = useCallback(
    (logicModifier: IPairingLogicModifier) => () => {
      // Toggle back to unselected if already selected. `selectedLM` is undefined whenever nothing is selected
      // (editor open, after a save, after Add New/Reset), so it must stay optional-chained - dereferencing it
      // throws and swallows the click.
      const newSelectedLM = selectedLM?._id === logicModifier._id ? undefined : logicModifier;
      updateLogicModifier(newSelectedLM);
    },
    [updateLogicModifier, selectedLM]
  );

  const logicModifiers = useMemo(() => {
    return storedPairing?.logicModifiers ?? [];
  }, [storedPairing]);

  /**
   * Fetch regulation-limit evaluation for the pairing. This is used to display which duty periods are in violation of regulations.
   * The evaluation is fetched when the "Flight Duty Period Display" toggle is turned on, and cleared when it is turned off.
   * It is held locally in state, not in redux, as it is only relevant to this component and doesn't need to be accessed globally.
   * It is passed into the DutyPeriodSummary component, which uses it to display regulation limit information for each duty period.
   */
  const getRegLimits = async (
    pairing: IPairing | undefined,
    draft: boolean = false
  ): Promise<IRegLimitEvaluation | null> => {
    if (pairing?._id == null) {return null;}
    return fetchRegLimitEvaluation(pairing, draft, dispatch);
  };

  // Fetch the regulation-limit evaluation when "Flight Duty Period Display" is turned ON; clear it when OFF.
  // We evaluate the LIVE editor state (`storedPairing`) as a draft (not the saved `initPairing`), so the shown
  // limits match what the user is looking at rather than reading as stale. This runs only when the toggle
  // flips (and on mount) - editing the pairing does NOT re-run it (the editor no longer remounts on edit; see
  // PairingEditorWindow's spinner overlay). An edit instead marks the panel stale, and the user re-runs the
  // evaluation with the "Recalculate Duty Limits" button.
  useEffect(() => {
    // `ignore` discards the result of a stale fetch if the inputs change (or the component unmounts) before
    // it resolves, so an out-of-order response can't overwrite newer state.
    let ignore = false;
    const loadRegLimits = async (): Promise<void> => {
      // Display off: just clear the limits and stop.
      if (!flightDutyPeriodDisplay) {
        setRegLimits(null);
        setRegLimitsPairing(null);
        return;
      }
      setRegLimitsLoading(true);
      // Awaiting unconditionally keeps every setState below off the synchronous effect path (so they aren't
      // flagged as direct state updates).
      const response: IRegLimitEvaluation | null = await getRegLimits(storedPairing, true);
      if (ignore) {return;}
      // Keep the previous data on a failed/empty fetch.
      if (response) {
        setRegLimits(response);
        setRegLimitsPairing(storedPairing ?? null);
      }
      setRegLimitsLoading(false);
    };

    void loadRegLimits();

    return () => {
      ignore = true;
    };
  }, [flightDutyPeriodDisplay]);

  const handleRefreshRegLimits = useCallback(async (): Promise<void> => {
    if (storedPairing?._id == null) {return;}
    setRegLimitsLoading(true);
    const response = await fetchRegLimitEvaluation(storedPairing, true, dispatch);
    if (response) {
      setRegLimits(response);
      setRegLimitsPairing(storedPairing);
    }
    setRegLimitsLoading(false);
  }, [storedPairing, dispatch]);

  // Track assigned crew so that it can be passed into posFlightChips (which are dragged onto bookings)
  const positions = storedPairing?.positions;
  const assignedCrew = useMemo(() => {
    return positions?.reduce((crewArray: IUser[], pos: IFullPairingPosition) => {
      if (pos.crew?._id != null) {
        crewArray.push(pos.crew);
      }
      return crewArray;
    }, []);
  }, [positions]);

  const bulkRemove = useCallback(() => {
    if (checkedEvents.length === 0) {return;}
    const eventGroups = _.cloneDeep(value);
    eventGroups.map((eventGroup: IPairingEditorEventGroup) => {
      eventGroup.flights = eventGroup.flights.filter((flight: IFlight) => !checkedEvents.includes(flight._id));
      eventGroup.positioningFlights = eventGroup.positioningFlights.filter(
        (posFlight: IPositioningFlight) => !checkedEvents.includes(posFlight._id)
      );
      eventGroup.nonFlyingAssignments = eventGroup.nonFlyingAssignments.filter(
        (nonFlight: INonFlyingAssignment) => !checkedEvents.includes(nonFlight._id)
      );
      eventGroup.groundEvents = eventGroup.groundEvents.filter(
        (groundEvent: IGroundEvent) => !checkedEvents.includes(groundEvent._id)
      );
      return eventGroup;
    });
    setSelectedRow({ anchorEl: null, row: null });
    setCheckedEvents([]);
    updateEventGroups(eventGroups);
  }, [checkedEvents, storedPairing, value]);

  const memoizedEventGroupTables = useCallback(
    (pendingFlights?: IFlight[]) => (
      <EventGroupTables
        theme={theme}
        pendingFlights={pendingFlights}
        eventGroupDisplay={eventGroupDisplay}
        selectedRow={selectedRow}
        handlePopoverOpen={handlePopoverOpen}
        storedPairing={storedPairing}
        handleDelete={handleDelete}
        handleLogicModifierClick={handleLogicModifierClick}
        selectedLM={selectedLM}
        hotels={hotels}
        restLocations={restLocations}
        updateHotels={updateHotels}
        restPeriodIndexDirty={restPeriodIndexDirty}
        assignedCrew={assignedCrew}
        restHasError={restHasError}
        rowHasError={rowHasError}
        displayAsUTC={displayAsUTC}
        bulkRemoveMode={bulkRemoveMode}
        setEventGroupDisplay={setEventGroupDisplay}
        checkedEvents={checkedEvents}
        setCheckedEvents={setCheckedEvents}
        updateEventGroups={updateEventGroups}
        id={id}
        flightDutyPeriodDisplay={flightDutyPeriodDisplay}
        regLimits={regLimits}
        regLimitsLoading={regLimitsLoading}
        regLimitsStale={regLimitsStale}
      />
    ),
    [
      theme,
      eventGroupDisplay,
      selectedRow,
      handlePopoverOpen,
      storedPairing,
      handleDelete,
      handleLogicModifierClick,
      selectedLM,
      hotels,
      restLocations,
      updateHotels,
      restPeriodIndexDirty,
      assignedCrew,
      restHasError,
      rowHasError,
      displayAsUTC,
      bulkRemoveMode,
      setEventGroupDisplay,
      checkedEvents,
      setCheckedEvents,
      updateEventGroups,
      id,
      flightDutyPeriodDisplay,
      regLimits,
      regLimitsLoading,
      regLimitsStale,
    ]
  );

  return (
    <OCPairingEventTable className={classNames(EventGroupsDirty && "dirty")} id={`eventgroup_table_${id}`}>
      {logicModifiers.length > 0 && (
        <Grid container direction="column">
          <Grid container>
            {logicModifiers.map((logicModifier: IPairingLogicModifier, index: number) =>
              logicModifier.dutyTime == null ? (
                <Grid
                  item
                  key={"LM" + index}
                  sx={{
                    minHeight: 30,
                  }}
                >
                  <Chip
                    size="small"
                    label={logicModifier.logicModifier.title}
                    onDelete={handleDelete(index)}
                    onClick={handleLogicModifierClick(logicModifier)}
                    clickable
                    sx={{
                      borderRadius: "3px",
                      fontWeight: 900,
                      fontSize: "0.8em",
                      backgroundColor: `#${logicModifier.logicModifier.color}`,
                      color: invertHexColor(logicModifier.logicModifier.color),
                      marginRight: "5px",
                      "& svg": {
                        fill: theme.palette.common.white,
                        color: theme.palette.common.white,
                      },
                    }}
                  />
                </Grid>
              ) : null
            )}
          </Grid>
        </Grid>
      )}
      <Grid container sx={{ justifyContent: "space-between" }}>
        <Grid item className="main_time_mode_selector">
          {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
          <SwitchField
            className={`time_mode_selector ${!displayAsUTC && "active"}`}
            name={"editflight_time_mode_selector"}
            value={displayAsUTC}
            onChange={() => setDisplayAsUTC(!displayAsUTC)}
            switchlabels={["UTC", "LOCAL"]}
            switchtooltips={["Times as UTC", "Times as local"]}
            label="Display Date/Times in"
            allowNull={false}
          />
          {flightDutyPeriodDisplay && (
            <Tooltip
              title={
                regLimitsStale
                  ? "Recalculate FDP limits for the current changes"
                  : "No unsaved changes to recalculate"
              }
            >
              <span>
                <Button
                  id={`${id}_refresh_reg_limits_btn`}
                  variant="contained"
                  color="secondary"
                  type="button"
                  size="small"
                  disabled={!regLimitsStale || regLimitsLoading}
                  onClick={() => void handleRefreshRegLimits()}
                  sx={{ mt: "6px" }}
                >
                  {regLimitsLoading ? "Recalculating..." : "Recalculate Duty Limits"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="flex-end">
            <Grid item>
              <Grid container alignItems="center" justifyContent="flex-end">
                <Grid item>
                  {bulkRemoveMode && (
                    <IconButton onClick={bulkRemove} disableRipple={true} sx={{ pt: 0, pb: 0, mb: "10px" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
                <Grid item className={"main_time_mode_selector"}>
                  <SwitchField
                    className={"time_mode_selector"}
                    name={"editflight_bulk_remove_mode"}
                    value={bulkRemoveMode}
                    onChange={() => setBulkRemoveMode(!bulkRemoveMode)}
                    switchlabels={["ON", "OFF"]}
                    switchtooltips={["Disable Bulk Flight Mode", "Enable Bulk Flight Mode"]}
                    label="Bulk Flight Removal"
                    allowNull={false}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item className={"main_time_mode_selector"}>
              <SwitchField
                className={"time_mode_selector"}
                name={"flight_duty_period_display"}
                value={flightDutyPeriodDisplay}
                onChange={() => setFlightDutyPeriodDisplay(!flightDutyPeriodDisplay)}
                switchlabels={["ON", "OFF"]}
                switchtooltips={["Disable Flight Duty Period Display", "Enable Flight Duty Period Display"]}
                label="Flight Duty Period Display"
                allowNull={false}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <FlightDropTarget id={id} updateEventGroups={updateEventGroups} eventGroupTables={memoizedEventGroupTables} />
      {eventMenu}
    </OCPairingEventTable>
  );
};
